import { useRef, useState } from "react";
import { useSnackbar } from "contexts/SnackbarContext";
import { uploadBucketImages, deleteBucketImage } from "api/bucketList";

// BE requests024: 항목당 활성 이미지 최대 10장, 파일당 최대 10MB, content_type 화이트리스트
export const IMAGE_MAX = 10;
const FILE_MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
];
const ACCEPT_ATTR = ACCEPT_TYPES.join(",");

/**
 * 버킷리스트 항목 이미지 갤러리 — 다중 업로드 + 단건 삭제 (완료/수정 시트).
 * @param {object} props
 * @param {object} props.item 대상 항목 (images 포함)
 * @param {function} props.onChanged 업로드/삭제 후 목록 새로고침 (Promise)
 */
const BucketImageGrid = ({ item, onChanged }) => {
    const { showError, showSuccess } = useSnackbar();
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [pendingPreviews, setPendingPreviews] = useState([]);
    const [deletingImageId, setDeletingImageId] = useState(null);

    const images = Array.isArray(item?.images) ? item.images : [];

    const handlePickFiles = () => fileInputRef.current?.click();

    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = ""; // 같은 파일 재선택 허용
        if (files.length === 0) return;

        if (images.length + files.length > IMAGE_MAX) {
            showError(`이미지는 최대 ${IMAGE_MAX}장까지 첨부할 수 있어요`);
            return;
        }
        const tooLarge = files.find((f) => f.size > FILE_MAX_BYTES);
        if (tooLarge) {
            showError("이미지 1장당 최대 10MB까지 첨부할 수 있어요");
            return;
        }
        const badType = files.find(
            (f) => f.type && !ACCEPT_TYPES.includes(f.type)
        );
        if (badType) {
            showError("jpg, png, webp, heic 이미지만 첨부할 수 있어요");
            return;
        }

        // 선택 즉시 로컬 썸네일(낙관적 미리보기) 생성 — 업로드 완료 후 서버 목록으로 교체
        const previews = files.map((file) => URL.createObjectURL(file));

        try {
            setIsUploading(true);
            setUploadProgress(0);
            setPendingPreviews(previews);
            await uploadBucketImages(item.id, files, {
                onUploadProgress: (evt) => {
                    if (evt.total) {
                        setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
                    }
                },
            });
            showSuccess("사진을 추가했어요");
            await onChanged?.();
        } catch (err) {
            const msg = err?.response?.data?.error;
            showError(typeof msg === "string" ? msg : "사진 업로드에 실패했어요");
        } finally {
            previews.forEach((url) => URL.revokeObjectURL(url));
            setPendingPreviews([]);
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteImage = async (imageId) => {
        if (deletingImageId) return;
        try {
            setDeletingImageId(imageId);
            await deleteBucketImage(item.id, imageId);
            await onChanged?.();
        } catch {
            showError("사진 삭제에 실패했어요");
        } finally {
            setDeletingImageId(null);
        }
    };

    return (
        <div className="bucket-detail__images">
            <span className="bucket-detail__section-label">
                사진 ({images.length}/{IMAGE_MAX})
            </span>
            <div className="bucket-detail__image-grid">
                {/* 추가 타일은 사진 유무와 무관하게 항상 맨 왼쪽(첫 번째)에 위치 */}
                {!isUploading && images.length < IMAGE_MAX && (
                    <button
                        type="button"
                        className="bucket-detail__image-add"
                        onClick={handlePickFiles}
                        aria-label="사진 추가"
                    >
                        ＋
                    </button>
                )}
                {images.map((img) => (
                    <div key={img.id} className="bucket-detail__image">
                        <img src={img.image_url} alt="" loading="lazy" />
                        <button
                            type="button"
                            className="bucket-detail__image-delete"
                            onClick={() => handleDeleteImage(img.id)}
                            disabled={deletingImageId === img.id}
                            aria-label="사진 삭제"
                        >
                            ✕
                        </button>
                    </div>
                ))}
                {pendingPreviews.map((url, idx) => (
                    <div
                        key={`pending-${idx}`}
                        className="bucket-detail__image bucket-detail__image--pending"
                    >
                        <img src={url} alt="" />
                        <span className="bucket-detail__image-loading" aria-hidden="true" />
                    </div>
                ))}
            </div>
            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                multiple
                hidden
                onChange={handleFilesSelected}
            />

            {isUploading && (
                <div
                    className="bucket-upload-overlay"
                    role="status"
                    aria-label="사진 업로드 중"
                >
                    <div className="bucket-upload-overlay__content">
                        <div className="bucket-upload-spinner">
                            <span className="bucket-upload-spinner__ring" />
                            <span className="bucket-upload-spinner__ring" />
                            <span className="bucket-upload-spinner__ring" />
                        </div>
                        <p className="bucket-upload-overlay__text">
                            업로드 중 {uploadProgress}%
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BucketImageGrid;
